import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { storefrontApiRequest, STOREFRONT_QUERY, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { Loader2, ShoppingCart, Plus, Minus, Package, Sparkles } from "lucide-react";
import { PACK_5_PRICE, PACK_10_PRICE, UNIT_PRICE } from "@/lib/bundlePricing";
import { useIsMobile } from "@/hooks/use-mobile";

const CONFETTI_COLORS = [
  "hsl(14, 78%, 55%)",
  "hsl(38, 80%, 55%)",
  "hsl(195, 60%, 50%)",
  "hsl(84, 40%, 55%)",
  "hsl(0, 85%, 60%)",
  "hsl(45, 95%, 60%)",
];

function launchMiniConfetti(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * 2;
  canvas.height = rect.height * 2;
  ctx.scale(2, 2);

  const particles: Array<{
    x: number; y: number; vx: number; vy: number;
    w: number; h: number; color: string; rotation: number; rv: number; alpha: number;
  }> = [];

  for (let i = 0; i < 60; i++) {
    particles.push({
      x: rect.width / 2 + (Math.random() - 0.5) * 80,
      y: rect.height * 0.3,
      vx: (Math.random() - 0.5) * 8,
      vy: -Math.random() * 6 - 2,
      w: Math.random() * 6 + 3,
      h: Math.random() * 4 + 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rotation: Math.random() * Math.PI * 2,
      rv: (Math.random() - 0.5) * 0.3,
      alpha: 1,
    });
  }

  let frame = 0;
  const animate = () => {
    ctx.clearRect(0, 0, rect.width, rect.height);
    let alive = false;
    for (const p of particles) {
      p.x += p.vx;
      p.vy += 0.15;
      p.y += p.vy;
      p.rotation += p.rv;
      p.alpha = Math.max(0, p.alpha - 0.012);
      if (p.alpha <= 0) continue;
      alive = true;
      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    frame++;
    if (alive && frame < 120) requestAnimationFrame(animate);
    else ctx.clearRect(0, 0, rect.width, rect.height);
  };
  requestAnimationFrame(animate);
}

interface PackSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packSize: number;
}

export const PackSelectorModal = ({ open, onOpenChange, packSize }: PackSelectorModalProps) => {
  const isMobile = useIsMobile();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selections, setSelections] = useState<Record<string, number>>({});
  const addItem = useCartStore(state => state.addItem);

  const packPrice = packSize === 10 ? PACK_10_PRICE : PACK_5_PRICE;
  const fullPrice = packSize * UNIT_PRICE;
  const savings = fullPrice - packPrice;

  const totalSelected = Object.values(selections).reduce((a, b) => a + b, 0);
  const remaining = packSize - totalSelected;
  const [showConfetti, setShowConfetti] = useState(false);
  const prevRemainingRef = useRef(remaining);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (remaining === 0 && prevRemainingRef.current > 0) {
      setShowConfetti(true);
      const canvas = confettiCanvasRef.current;
      if (canvas) launchMiniConfetti(canvas);
      const timer = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(timer);
    }
    prevRemainingRef.current = remaining;
  }, [remaining]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelections({});
    storefrontApiRequest(STOREFRONT_QUERY, { first: 20 })
      .then((data) => {
        const filtered = data?.data?.products?.edges?.filter(
          (p: ShopifyProduct) => p.node.handle !== "don-solidaire"
        ) || [];
        setProducts(filtered);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open]);

  const updateQty = useCallback((productId: string, delta: number) => {
    setSelections(prev => {
      const current = prev[productId] || 0;
      const next = Math.max(0, current + delta);
      const totalOthers = Object.entries(prev)
        .filter(([k]) => k !== productId)
        .reduce((a, [, v]) => a + v, 0);
      if (next + totalOthers > packSize) return prev;
      if (next === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: next };
    });
  }, [packSize]);

  const handleConfirm = async () => {
    if (remaining !== 0) return;
    setAdding(true);
    try {
      for (const product of products) {
        const qty = selections[product.node.id];
        if (!qty || qty <= 0) continue;
        const variant = product.node.variants.edges[0]?.node;
        if (!variant?.availableForSale) continue;
        await addItem({
          product,
          variantId: variant.id,
          variantTitle: variant.title,
          price: variant.price,
          quantity: qty,
          selectedOptions: variant.selectedOptions || [],
          productType: 'sticker',
        });
      }
      toast.success(`Pack ${packSize} ajouté au panier ! 🎉`, { position: "top-center" });
      onOpenChange(false);
    } catch (err) {
      console.error("Pack add error:", err);
      toast.error("Erreur lors de l'ajout au panier");
    } finally {
      setAdding(false);
    }
  };

  const innerContent = (
    <>
      {/* Confetti canvas */}
      <canvas
        ref={confettiCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-50"
        style={{ opacity: showConfetti ? 1 : 0, transition: "opacity 0.3s" }}
      />

      {/* Progress bar */}
      <div className="space-y-2 px-1">
        <div className="flex justify-between items-center text-sm font-body">
          <span className="text-muted-foreground">
            {totalSelected} / {packSize} sélectionnés
          </span>
          {remaining > 0 && (
            <span className="text-primary font-medium animate-pulse">
              Encore {remaining} à choisir
            </span>
          )}
          {remaining === 0 && (
            <span className="text-green-600 font-medium flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Pack complet !
            </span>
          )}
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(var(--terracotta-glow))] transition-all duration-500 ease-out"
            style={{ width: `${(totalSelected / packSize) * 100}%` }}
          />
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1 mt-3">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          products.map((product) => {
            const image = product.node.images.edges[0]?.node;
            const variant = product.node.variants.edges[0]?.node;
            const qty = selections[product.node.id] || 0;
            const isAvailable = variant?.availableForSale;

            return (
              <div
                key={product.node.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-300 ${
                  qty > 0
                    ? "border-primary/30 bg-primary/5 shadow-sm"
                    : "border-border hover:border-primary/15 bg-card/50"
                } ${!isAvailable ? "opacity-50" : ""}`}
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                  {image ? (
                    <img
                      src={`${image.url}&width=112`}
                      alt={image.altText || product.node.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Package className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-sm leading-snug">{product.node.title}</h4>
                  <p className="text-xs text-muted-foreground font-body">
                    {isAvailable ? "En stock" : "Rupture de stock"}
                  </p>
                </div>
                {isAvailable && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => updateQty(product.node.id, -1)}
                      disabled={qty === 0}
                      className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className={`w-8 text-center font-display font-bold text-sm tabular-nums ${qty > 0 ? "text-primary" : "text-muted-foreground"}`}>
                      {qty}
                    </span>
                    <button
                      onClick={() => updateQty(product.node.id, 1)}
                      disabled={remaining <= 0}
                      className="w-8 h-8 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-center text-primary hover:bg-primary/15 disabled:opacity-30 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="pt-3 border-t border-border space-y-3">
        <div className="flex justify-between items-center font-body text-sm">
          <span className="text-muted-foreground">Total pack</span>
          <div className="flex items-center gap-2">
            <span className="line-through text-muted-foreground text-xs">{fullPrice.toFixed(2)}$</span>
            <span className="text-lg font-display font-bold text-primary">{packPrice.toFixed(2)}$</span>
          </div>
        </div>
        <Button
          onClick={handleConfirm}
          disabled={remaining !== 0 || adding}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-warm font-display"
          size="lg"
        >
          {adding ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <ShoppingCart className="w-4 h-4 mr-2" />
          )}
          {remaining > 0
            ? `Encore ${remaining} adhésif${remaining > 1 ? "s" : ""} à choisir`
            : "Ajouter au panier"}
        </Button>
      </div>
    </>
  );

  // Mobile: use Drawer (bottom sheet) for better touch UX
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92dvh] flex flex-col px-4 pb-6">
          <DrawerHeader className="text-left px-0">
            <DrawerTitle className="font-display flex items-center gap-2 text-xl">
              <Package className="w-5 h-5 text-primary" />
              Composez votre Pack {packSize}
            </DrawerTitle>
            <DrawerDescription className="font-body">
              Choisissez {packSize} adhésifs parmi nos modèles. Économisez{" "}
              <span className="font-semibold text-primary">{savings.toFixed(2)}$</span> !
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-hidden flex flex-col relative">
            {innerContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: use Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] !flex !flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            <Package className="w-5 h-5 text-primary" />
            Composez votre Pack {packSize}
          </DialogTitle>
          <DialogDescription className="font-body">
            Choisissez {packSize} adhésifs parmi nos modèles. Économisez{" "}
            <span className="font-semibold text-primary">{savings.toFixed(2)}$</span> !
          </DialogDescription>
        </DialogHeader>
        {innerContent}
      </DialogContent>
    </Dialog>
  );
};
